import type { IJobQueueService, JobContext, JobOptions } from "./types";
import type { BatchOperation, BatchJobStatus, Batch } from "./batch-schemas";
import { JOB_STATUS } from "./schemas";
import type { Logger } from "@brains/utils/logger";
import {
  Effect,
  Exit,
  Fiber,
  FiberSet,
  Schedule,
  Scope,
} from "@brains/utils/effect";
import type { Clock } from "@brains/utils/effect";

const TERMINAL_BATCH_RETENTION_MS = 24 * 60 * 60 * 1000;
const CLEANUP_INTERVAL_MS = 60 * 60 * 1000;

interface BatchJobManagerOptions {
  /** Internal clock boundary used for deterministic scheduling tests. */
  clock?: Clock.Clock;
}

interface CleanupSupervisor {
  scope: Scope.CloseableScope;
  fibers: FiberSet.FiberSet<void, never>;
}

type BatchManagerTransitionKind = "start" | "stop";

interface BatchManagerTransition {
  kind: BatchManagerTransitionKind;
  intervalMs: number;
  promise: Promise<void>;
  resolve: () => void;
  reject: (error: unknown) => void;
}

/**
 * Batch job manager for tracking groups of related jobs
 *
 * This manager tracks multiple related jobs as a logical batch without
 * creating a special "batch-operation" job type. Instead, it monitors
 * the individual jobs and provides aggregated status.
 */
export class BatchJobManager {
  private jobQueue: IJobQueueService;
  private logger: Logger;
  // In-memory tracking of batch metadata
  // In production, this could be stored in a database table
  private batches = new Map<
    string,
    {
      jobIds: string[];
      operations: BatchOperation[];
      source: string;
      startedAt: string;
      metadata: JobContext;
      // First Date.now() at which this batch was observed terminal via
      // getBatchStatus. Lets cleanup() decide on a batch without re-fetching
      // every job's status. Stays unset for batches nobody has observed.
      terminalAt?: number;
    }
  >();

  // Fiber that sweeps terminal batches older than the retention window. Runs
  // independently of enqueue activity so the map stays bounded even when no
  // new batches are arriving.
  private cleanupFiber: Fiber.RuntimeFiber<void, never> | null = null;
  private cleanupSupervisor: CleanupSupervisor | null = null;
  private readonly inFlightCleanups = new Set<Promise<number>>();
  private activeTransition: BatchManagerTransition | null = null;
  private readonly transitionQueue: BatchManagerTransition[] = [];
  private acceptingCleanup = true;
  private readonly clock: Clock.Clock | undefined;

  public static createFresh(
    jobQueue: IJobQueueService,
    logger: Logger,
    options?: BatchJobManagerOptions,
  ): BatchJobManager {
    return new BatchJobManager(jobQueue, logger, options);
  }

  private constructor(
    jobQueue: IJobQueueService,
    logger: Logger,
    options?: BatchJobManagerOptions,
  ) {
    this.jobQueue = jobQueue;
    this.logger = logger;
    this.clock = options?.clock;
  }

  /** Start the periodic cleanup fiber. Idempotent across repeated calls. */
  public start(intervalMs: number = CLEANUP_INTERVAL_MS): Promise<void> {
    return this.requestTransition("start", intervalMs);
  }

  public stop(): Promise<void> {
    return this.requestTransition("stop", CLEANUP_INTERVAL_MS);
  }

  private async startCleanup(intervalMs: number): Promise<void> {
    this.acceptingCleanup = true;
    if (this.cleanupFiber) return;
    this.cleanupFiber = Effect.runFork(this.runCleanupLoop(intervalMs));
  }

  private runCleanupLoop(intervalMs: number): Effect.Effect<void> {
    const scheduledCleanup = this.cleanupTerminalBatchMetadata().pipe(
      Effect.schedule(Schedule.spaced(intervalMs)),
      Effect.asVoid,
    );
    return this.clock
      ? Effect.withClock(scheduledCleanup, this.clock)
      : scheduledCleanup;
  }

  private scheduleTerminalBatchCleanup(): void {
    if (!this.acceptingCleanup) return;
    this.cleanupSupervisor ??= this.createCleanupSupervisor();
    const fiber = Effect.runFork(this.cleanupTerminalBatchMetadata());
    FiberSet.unsafeAdd(this.cleanupSupervisor.fibers, fiber);
  }

  private async stopCleanup(): Promise<void> {
    this.acceptingCleanup = false;
    const cleanupFiber = this.cleanupFiber;
    this.cleanupFiber = null;
    if (cleanupFiber) {
      await Effect.runPromise(Fiber.interrupt(cleanupFiber));
    }

    const cleanupSupervisor = this.cleanupSupervisor;
    if (cleanupSupervisor) {
      await Effect.runPromise(FiberSet.awaitEmpty(cleanupSupervisor.fibers));
    }
    while (this.inFlightCleanups.size > 0) {
      await Promise.allSettled(this.inFlightCleanups);
    }
    if (cleanupSupervisor) {
      await Effect.runPromise(Scope.close(cleanupSupervisor.scope, Exit.void));
      this.cleanupSupervisor = null;
    }
  }

  private requestTransition(
    kind: BatchManagerTransitionKind,
    intervalMs: number,
  ): Promise<void> {
    const tail = this.transitionQueue.at(-1) ?? this.activeTransition;
    if (tail?.kind === kind) return tail.promise;

    let resolveTransition: () => void = () => undefined;
    let rejectTransition: (error: unknown) => void = () => undefined;
    const promise = new Promise<void>((resolve, reject) => {
      resolveTransition = resolve;
      rejectTransition = reject;
    });
    const transition: BatchManagerTransition = {
      kind,
      intervalMs,
      promise,
      resolve: resolveTransition,
      reject: rejectTransition,
    };
    this.transitionQueue.push(transition);
    this.runNextTransition();
    return promise;
  }

  private runNextTransition(): void {
    if (this.activeTransition) return;
    const transition = this.transitionQueue.shift();
    if (!transition) return;
    this.activeTransition = transition;

    const operation =
      transition.kind === "start"
        ? this.startCleanup(transition.intervalMs)
        : this.stopCleanup();
    void operation.then(
      () => this.completeTransition(transition, true),
      (error: unknown) => this.completeTransition(transition, false, error),
    );
  }

  private completeTransition(
    transition: BatchManagerTransition,
    succeeded: boolean,
    error?: unknown,
  ): void {
    if (this.activeTransition !== transition) return;
    this.activeTransition = null;
    if (succeeded) {
      transition.resolve();
    } else {
      transition.reject(error);
    }
    queueMicrotask(() => this.runNextTransition());
  }

  private createCleanupSupervisor(): CleanupSupervisor {
    const scope = Effect.runSync(Scope.make());
    const fibers = Effect.runSync(
      Scope.extend(FiberSet.make<void, never>(), scope),
    );
    return { scope, fibers };
  }

  private cleanupTerminalBatchMetadata(): Effect.Effect<void> {
    return Effect.tryPromise({
      try: () => {
        const cleanup = this.cleanup(TERMINAL_BATCH_RETENTION_MS);
        this.inFlightCleanups.add(cleanup);
        void cleanup.then(
          () => this.inFlightCleanups.delete(cleanup),
          () => this.inFlightCleanups.delete(cleanup),
        );
        return cleanup;
      },
      catch: (error) => error,
    }).pipe(
      Effect.asVoid,
      Effect.catchAll((error) =>
        Effect.sync(() => {
          this.logger.warn("Failed to clean up terminal batch metadata", {
            error,
          });
        }),
      ),
    );
  }

  private validateOperations(operations: BatchOperation[]): void {
    for (const operation of operations) {
      // Enqueue-side preflight uses the declared validator, not an executable
      // handler — web processes register validation-only and hold no handlers.
      const validator = this.jobQueue.getValidator(operation.type);
      if (!validator) {
        throw new Error(`No job type declared: ${operation.type}`);
      }

      if (validator.validateAndParse(operation.data) === null) {
        throw new Error(`Invalid job data for type: ${operation.type}`);
      }
    }
  }

  /**
   * Enqueue a batch of operations as individual jobs
   */
  async enqueueBatch(
    operations: BatchOperation[],
    options: JobOptions,
    batchId: string,
    _pluginId?: string,
  ): Promise<string> {
    if (operations.length === 0) {
      throw new Error("Cannot enqueue empty batch");
    }

    this.validateOperations(operations);

    const jobIds: string[] = [];

    // Register before enqueueing so a fast child completing mid-enqueue can
    // resolve its batch. jobIds is shared with the entry and fills as jobs
    // are enqueued; getBatchStatus counts not-yet-enqueued operations as
    // active so the batch can't look terminal early.
    this.batches.set(batchId, {
      jobIds,
      operations,
      source: options.source,
      startedAt: new Date().toISOString(),
      metadata: {
        ...options.metadata,
        rootJobId: batchId,
      },
    });

    try {
      // Enqueue each operation as an individual job
      for (const operation of operations) {
        // Build job options for each individual job
        // Set rootJobId to batchId so CLI progress tracking works through inheritance
        const jobOptions: JobOptions = {
          ...options,
          rootJobId: batchId, // Individual jobs inherit from batch
          metadata: {
            ...options.metadata,
            operationTarget: operation.type,
          },
        };

        const jobId = await this.jobQueue.enqueue({
          type: operation.type,
          data: operation.data,
          options: jobOptions,
        });
        jobIds.push(jobId);
      }

      this.logger.debug("Enqueued batch operations", {
        batchId,
        operationCount: operations.length,
        jobIds,
        rootJobId: batchId,
      });

      this.scheduleTerminalBatchCleanup();

      return batchId;
    } catch (error) {
      this.batches.delete(batchId);
      this.logger.error("Failed to enqueue batch operations", {
        error,
        operationCount: operations.length,
        enqueuedJobs: jobIds.length,
      });
      throw error;
    }
  }

  /**
   * Get batch status by aggregating individual job statuses
   */
  async getBatchStatus(batchId: string): Promise<BatchJobStatus | null> {
    const batch = this.batches.get(batchId);
    if (!batch) {
      return null;
    }

    try {
      // Get status of all jobs in the batch
      const jobStatuses = await Promise.all(
        batch.jobIds.map((jobId) => this.jobQueue.getStatus(jobId)),
      );

      // Count statuses
      let completedOperations = 0;
      let failedOperations = 0;
      let activeOperations = 0;
      const errors: string[] = [];

      for (const [index, job] of jobStatuses.entries()) {
        if (!job) {
          failedOperations++;
          const operation = batch.operations[index];
          const jobId = batch.jobIds[index] ?? "unknown";
          errors.push(
            `Missing job ${jobId}${operation ? ` for ${operation.type}` : ""}`,
          );
          continue;
        }

        switch (job.status) {
          case "completed":
            completedOperations++;
            break;
          case "failed":
            failedOperations++;
            if (job.lastError) {
              errors.push(job.lastError);
            }
            break;
          case "processing":
          case "pending":
            activeOperations++;
            break;
        }
      }

      // Operations not yet enqueued (registration happens before enqueueing)
      // count as active so a mid-enqueue batch can't look terminal
      activeOperations += batch.operations.length - batch.jobIds.length;

      let status: (typeof JOB_STATUS)[keyof typeof JOB_STATUS];
      if (activeOperations > 0) {
        status = JOB_STATUS.PROCESSING;
      } else if (failedOperations > 0) {
        status = JOB_STATUS.FAILED;
      } else {
        status = JOB_STATUS.COMPLETED;
      }

      if (
        batch.terminalAt === undefined &&
        (status === JOB_STATUS.COMPLETED || status === JOB_STATUS.FAILED)
      ) {
        batch.terminalAt = Date.now();
      }

      // Find current operation (first non-completed job)
      let currentOperation: string | undefined;
      for (let i = 0; i < batch.jobIds.length; i++) {
        const job = jobStatuses[i];
        if (job && job.status !== "completed" && job.status !== "failed") {
          const operation = batch.operations[i];
          if (operation) {
            currentOperation = `Processing ${operation.type}`;
          }
          break;
        }
      }

      return {
        batchId,
        totalOperations: batch.operations.length,
        completedOperations,
        failedOperations,
        errors,
        status,
        currentOperation,
        // Include original batch metadata for routing context
        metadata: batch.metadata,
      };
    } catch (error) {
      this.logger.error("Failed to get batch status", { batchId, error });
      throw error;
    }
  }

  /**
   * Clean up old batch metadata.
   *
   * Fast path: batches whose terminal status was already observed via
   * `getBatchStatus` carry a `terminalAt` timestamp; we drop them as soon as
   * that timestamp is older than the cutoff, with no DB reads.
   *
   * Slow path: batches that nobody has ever observed fall back to fetching
   * status once they are at least cutoff-old, matching the pre-memoization
   * behavior. This keeps un-observed terminal batches from leaking forever.
   */
  /**
   * Retention bookkeeping for one batch, or undefined once it has been swept.
   *
   * terminalAt and an entry's presence are internal to cleanup(), and no other
   * public method reports either, so a test asserting on the retention window
   * had to reach into the map. A read-only view keeps that check possible
   * without exposing the entry itself.
   */
  inspectRetention(
    batchId: string,
  ): { readonly terminalAt: number | undefined } | undefined {
    const entry = this.batches.get(batchId);
    return entry ? { terminalAt: entry.terminalAt } : undefined;
  }

  async cleanup(olderThanMs: number): Promise<number> {
    const cutoffTime = Date.now() - olderThanMs;
    let cleaned = 0;

    for (const [batchId, batch] of this.batches.entries()) {
      if (batch.terminalAt !== undefined) {
        if (batch.terminalAt <= cutoffTime) {
          this.batches.delete(batchId);
          cleaned++;
        }
        continue;
      }

      const batchTime = new Date(batch.startedAt).getTime();
      if (batchTime <= cutoffTime) {
        const status = await this.getBatchStatus(batchId);
        if (
          status &&
          (status.status === JOB_STATUS.COMPLETED ||
            status.status === JOB_STATUS.FAILED)
        ) {
          this.batches.delete(batchId);
          cleaned++;
        }
      }
    }

    this.logger.debug("Cleaned up batch metadata", { cleaned });
    return cleaned;
  }

  /**
   * Register batch metadata for tracking (used when jobs are enqueued separately)
   */
  public registerBatch(
    batchId: string,
    jobIds: string[],
    operations: BatchOperation[],
    source: string,
    metadata: JobContext,
  ): void {
    this.batches.set(batchId, {
      jobIds,
      operations,
      source,
      startedAt: new Date().toISOString(),
      metadata,
    });

    this.logger.debug("Registered batch metadata", {
      batchId,
      operationCount: operations.length,
      jobIds,
    });
  }

  /**
   * Get all active batches (pending or processing)
   */
  async getActiveBatches(): Promise<Batch[]> {
    const activeBatches: Batch[] = [];

    try {
      // Check each batch's status
      for (const [batchId, metadata] of this.batches) {
        const status = await this.getBatchStatus(batchId);

        if (
          status &&
          (status.status === "pending" || status.status === "processing")
        ) {
          activeBatches.push({
            batchId,
            status,
            metadata: {
              operations: metadata.operations,
              source: metadata.source,
              startedAt: metadata.startedAt,
              metadata: metadata.metadata,
            },
          });
        }
      }

      this.logger.debug("Retrieved active batches", {
        count: activeBatches.length,
      });

      return activeBatches;
    } catch (error) {
      this.logger.error("Failed to get active batches", { error });
      throw error;
    }
  }
}
